import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiMail, FiPhone, FiMapPin, FiArrowRight, FiLinkedin, FiTwitter, FiFacebook } from 'react-icons/fi';
import { api } from '../utils/api';
import { useRefreshListener, REFRESH_EVENTS } from '../utils/refreshEvents';
import './Footer.css';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [settings, setSettings] = useState(null);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await api.getSettings();
      setSettings(res.data);
    } catch (error) {
      console.error('Erreur lors du chargement des paramètres:', error);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Écouter les événements de rafraîchissement depuis l'admin
  useRefreshListener(REFRESH_EVENTS.SETTINGS, fetchSettings);

  if (!settings) {
    // Valeurs par défaut pendant le chargement
    return (
      <footer className="footer-ux">
        <div className="footer-background"></div>
        <div className="container-ux">
          <div className="footer-content-ux">
            <motion.div className="footer-section-ux">
              <div className="footer-brand">
                <div className="footer-logo">
                  <span className="footer-logo-text">VH</span>
                </div>
                <h3 className="footer-brand-name">VirtualHand</h3>
              </div>
              <p className="footer-description">
                Chef de Projet, Assistante Indépendante. Libérez du temps, 
                gagnez en confiance, réalisez vos projets.
              </p>
            </motion.div>
          </div>
        </div>
      </footer>
    );
  }

  const footerLinks = {
    navigation: [
      { to: '/', label: 'Accueil' },
      { to: '/services', label: 'Services' },
      { to: '/tarifs', label: 'Tarifs' },
      { to: '/contact', label: 'Contact' }
    ],
    legal: [
      { to: '#', label: 'Mentions légales' },
      { to: '#', label: 'Politique de confidentialité' },
      { to: '#', label: 'CGV' }
    ]
  };

  return (
    <footer className="footer-ux">
      <div className="footer-background"></div>
      <div className="container-ux">
        <div className="footer-content-ux">
          {/* Brand Section */}
          <motion.div
            className="footer-section-ux"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="footer-brand">
              {settings.logo ? (
                <img src={settings.logo} alt={settings.siteName} className="footer-logo-image" />
              ) : (
                <div className="footer-logo">
                  <span className="footer-logo-text">VH</span>
                </div>
              )}
              <h3 className="footer-brand-name">{settings.siteName}</h3>
            </div>
            <p className="footer-description">
              {settings.footer.description}
            </p>
            <div className="footer-social">
              {settings.footer.socialLinks.linkedin && (
                <a href={settings.footer.socialLinks.linkedin} className="social-link" aria-label="LinkedIn" target="_blank" rel="noopener noreferrer">
                  <FiLinkedin />
                </a>
              )}
              {settings.footer.socialLinks.twitter && (
                <a href={settings.footer.socialLinks.twitter} className="social-link" aria-label="Twitter" target="_blank" rel="noopener noreferrer">
                  <FiTwitter />
                </a>
              )}
              {settings.footer.socialLinks.facebook && (
                <a href={settings.footer.socialLinks.facebook} className="social-link" aria-label="Facebook" target="_blank" rel="noopener noreferrer">
                  <FiFacebook />
                </a>
              )}
            </div>
          </motion.div>

          {/* Navigation */}
          <motion.div
            className="footer-section-ux"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h4 className="footer-title">Navigation</h4>
            <ul className="footer-links">
              {footerLinks.navigation.map((link, index) => (
                <li key={index}>
                  <Link to={link.to} className="footer-link">
                    <FiArrowRight className="footer-link-icon" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Legal */}
          <motion.div
            className="footer-section-ux"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h4 className="footer-title">Informations</h4>
            <ul className="footer-links">
              {footerLinks.legal.map((link, index) => (
                <li key={index}>
                  <Link to={link.to} className="footer-link">
                    <FiArrowRight className="footer-link-icon" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact */}
          <motion.div
            className="footer-section-ux"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <h4 className="footer-title">Contact</h4>
            <ul className="footer-contact">
              <li>
                <a href={`tel:${settings.footer.phone.replace(/\s/g, '')}`} className="footer-contact-item">
                  <div className="contact-icon-wrapper">
                    <FiPhone />
                  </div>
                  <div>
                    <span className="contact-label">Téléphone</span>
                    <span className="contact-value">{settings.footer.phone}</span>
                  </div>
                </a>
              </li>
              <li>
                <a href={`mailto:${settings.footer.email}`} className="footer-contact-item">
                  <div className="contact-icon-wrapper">
                    <FiMail />
                  </div>
                  <div>
                    <span className="contact-label">Email</span>
                    <span className="contact-value">{settings.footer.email}</span>
                  </div>
                </a>
              </li>
              <li>
                <div className="footer-contact-item">
                  <div className="contact-icon-wrapper">
                    <FiMapPin />
                  </div>
                  <div>
                    <span className="contact-label">Localisation</span>
                    <span className="contact-value">{settings.footer.location}</span>
                  </div>
                </div>
              </li>
            </ul>
          </motion.div>
        </div>

        <motion.div
          className="footer-bottom-ux"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <p>&copy; {currentYear} {settings.siteName}. Tous droits réservés.</p>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
